import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Key, Plus, Copy, Check, Trash2, AlertTriangle,
  BookOpen, ExternalLink, Loader2, Zap, XCircle, CheckCircle2,
} from "lucide-react";
import { getErrorMessage } from "../utils/errorHandler";
import SessionSecurityCard from "../components/SessionSecurityCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function auth() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
}

export default function ApiKeys() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  // Shown ONCE after creation — full key in cleartext
  const [revealedKey, setRevealedKey] = useState(null);
  const [copiedKeyId, setCopiedKeyId] = useState(null);
  // Per-key test connection state: { [key_id]: { loading, result, error } }
  const [testState, setTestState] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api-keys`, { headers: auth() });
      setKeys(res.data.keys || []);
    } catch (err) {
      toast.error(getErrorMessage(err, t("load_failed") || "Load failed"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!newKeyName.trim()) { toast.error(t("name_required") || "Name is required"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api-keys`,
        { name: newKeyName.trim() },
        { headers: auth() });
      setRevealedKey(res.data);
      setCreateOpen(false);
      setNewKeyName("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, t("create_failed") || "Failed to create key"));
    } finally { setCreating(false); }
  };

  const revoke = async (key_id) => {
    if (!window.confirm(t("confirm_revoke_key") || "Revoke this API key? The partner app will lose access immediately.")) return;
    try {
      await axios.delete(`${API}/api-keys/${key_id}`, { headers: auth() });
      toast.success(t("key_revoked") || "Key revoked");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, t("revoke_failed") || "Revoke failed"));
    }
  };

  const copyKey = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKeyId(id);
      toast.success(t("copied") || "Copied!");
      setTimeout(() => setCopiedKeyId(null), 1500);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const testConnection = async (key_id) => {
    setTestState((s) => ({ ...s, [key_id]: { loading: true, result: null, error: null } }));
    try {
      const res = await axios.post(
        `${API}/api-keys/${key_id}/test`,
        {},
        { headers: auth() }
      );
      setTestState((s) => ({ ...s, [key_id]: { loading: false, result: res.data, error: null } }));
      if (res.data?.ok) {
        toast.success(t("connection_ok") || "Connection OK");
      } else {
        toast.error(res.data?.message || (t("connection_failed") || "Connection failed"));
      }
    } catch (err) {
      setTestState((s) => ({ ...s, [key_id]: { loading: false, result: null, error: getErrorMessage(err, t("connection_failed") || "Connection failed") } }));
      toast.error(getErrorMessage(err, t("connection_failed") || "Connection failed"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 dark:from-slate-950 dark:via-slate-900 dark:to-amber-950/20">
      <header className="sticky top-0 z-30 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> {t("back") || "Back"}
            </Button>
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              <h1 className="text-lg font-bold" data-testid="api-keys-title">
                {t("api_keys") || "API Keys"}
              </h1>
              <Badge variant="outline" className="text-[10px]">BETA</Badge>
            </div>
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`}
            target="_blank" rel="noopener noreferrer"
            className="text-xs text-amber-600 hover:underline flex items-center gap-1"
            data-testid="api-docs-link"
          >
            <BookOpen className="w-3 h-3" />
            {t("api_docs") || "API Docs"} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <SessionSecurityCard />
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Key className="w-5 h-5 text-amber-500" />
                  {t("your_api_keys") || "Your API Keys"}
                </CardTitle>
                <CardDescription>
                  {t("api_keys_desc") ||
                    "Connect partner apps (like Lumera) to SlideCon. Each key acts on behalf of your account."}
                </CardDescription>
              </div>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="create-key-btn">
                    <Plus className="w-4 h-4" />
                    {t("create_key") || "Create Key"}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("create_api_key") || "Create API Key"}</DialogTitle>
                    <DialogDescription>
                      {t("create_key_desc") ||
                        "Give your key a name so you can identify it later (e.g., 'Lumera Integration')."}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-2">
                    <Label>{t("key_name") || "Key name"}</Label>
                    <Input
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g., Lumera Integration"
                      onKeyDown={(e) => e.key === "Enter" && create()}
                      data-testid="key-name-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateOpen(false)}>
                      {t("cancel") || "Cancel"}
                    </Button>
                    <Button onClick={create} disabled={creating} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-create-key-btn">
                      {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      {t("create") || "Create"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            ) : keys.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-500" data-testid="empty-keys">
                <Key className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>{t("no_keys_yet") || "You don't have any API keys yet."}</p>
                <p className="text-xs mt-1">{t("no_keys_hint") || "Create one to start connecting partner apps."}</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="api-keys-list">
                {keys.map((k) => (
                  <div
                    key={k.key_id}
                    className={`p-3 rounded-lg border ${k.revoked_at ? "bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 opacity-60" : "bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"}`}
                    data-testid={`api-key-row-${k.key_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{k.name}</h4>
                          {k.revoked_at && (
                            <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-500">REVOKED</Badge>
                          )}
                        </div>
                        <code className="text-xs font-mono text-slate-500 block mt-1">
                          {k.prefix}…
                        </code>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                          <span>{k.request_count || 0} {t("requests") || "requests"}</span>
                          <span>{t("rate_limit") || "Limit"}: {k.rate_limit_per_min}/min</span>
                          {k.last_used_at && <span>{t("last_used") || "Last used"}: {new Date(k.last_used_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      {!k.revoked_at && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => testConnection(k.key_id)}
                            disabled={testState[k.key_id]?.loading}
                            data-testid={`test-${k.key_id}`}
                            className="text-emerald-600 border-emerald-500/40 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                          >
                            {testState[k.key_id]?.loading
                              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              : <Zap className="w-4 h-4 mr-1" />}
                            {t("test") || "Test"}
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            className="text-red-500 hover:bg-red-50 hover:text-red-600"
                            onClick={() => revoke(k.key_id)}
                            data-testid={`revoke-${k.key_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            {t("revoke") || "Revoke"}
                          </Button>
                        </div>
                      )}
                    </div>
                    {/* Test connection result strip — only after a test runs */}
                    {testState[k.key_id]?.result && (
                      <div
                        className={`mt-2 p-2 rounded text-xs flex items-start gap-2 ${
                          testState[k.key_id].result.ok
                            ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
                            : "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border border-amber-500/30"
                        }`}
                        data-testid={`test-result-${k.key_id}`}
                      >
                        {testState[k.key_id].result.ok
                          ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          {testState[k.key_id].result.ok ? (
                            <>
                              <div className="font-semibold">
                                {t("connection_ok") || "Connection OK"} — GET /api/v1/me
                              </div>
                              <div className="text-[11px] opacity-90 truncate">
                                {testState[k.key_id].result.user?.email}
                                {" · "}
                                {t("rate_limit") || "Limit"}: {testState[k.key_id].result.rate_limit_per_min}/min
                                {" · "}
                                {new Date(testState[k.key_id].result.checked_at).toLocaleTimeString()}
                              </div>
                            </>
                          ) : (
                            <div>{testState[k.key_id].result.message || (t("connection_failed") || "Connection failed")}</div>
                          )}
                        </div>
                      </div>
                    )}
                    {testState[k.key_id]?.error && (
                      <div
                        className="mt-2 p-2 rounded text-xs flex items-start gap-2 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-500/30"
                        data-testid={`test-error-${k.key_id}`}
                      >
                        <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>{testState[k.key_id].error}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quickstart docs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("quickstart") || "Quickstart"}</CardTitle>
            <CardDescription>
              {t("quickstart_desc") || "Send a sermon PDF from your partner app; receive a ready-to-edit slideshow URL."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  {t("send_sermon_curl") || "Send a sermon PDF (curl)"}
                </h4>
                <Button
                  variant="ghost" size="sm" className="h-6 text-[10px] gap-1"
                  onClick={() => {
                    const snippet = `curl -X POST ${process.env.REACT_APP_BACKEND_URL}/api/v1/projects/from-pdf \\\n  -H "X-API-Key: slcn_live_..." \\\n  -F "file=@sermon.pdf" \\\n  -F "title=Sunday Sermon" \\\n  -F "source_app=lumera"`;
                    navigator.clipboard.writeText(snippet);
                    toast.success(t("copied") || "Copied!");
                  }}
                  data-testid="copy-curl-snippet"
                >
                  <Copy className="w-3 h-3" /> {t("copy") || "Copy"}
                </Button>
              </div>
              <pre className="text-[11px] bg-slate-950 text-slate-100 p-3 rounded-lg overflow-x-auto" data-testid="quickstart-snippet">
{`# Send a sermon PDF → get back an editable slide deck
curl -X POST ${process.env.REACT_APP_BACKEND_URL}/api/v1/projects/from-pdf \\
  -H "X-API-Key: slcn_live_..." \\
  -F "file=@sermon.pdf" \\
  -F "title=Sunday Sermon" \\
  -F "source_app=lumera"

# Response (open edit_url in a new tab):
# {
#   "project_id": "proj_abc123",
#   "edit_url":   "${process.env.REACT_APP_BACKEND_URL}/editor/proj_abc123",
#   "view_url":   "${process.env.REACT_APP_BACKEND_URL}/present/proj_abc123",
#   "slide_count": 8
# }`}
              </pre>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  {t("share_to_slidecon_button") || "Drop-in button for your app (HTML/JS)"}
                </h4>
                <Button
                  variant="ghost" size="sm" className="h-6 text-[10px] gap-1"
                  onClick={() => {
                    const html = `<!-- Share-to-SlideCon button — drop into any HTML page -->
<input type="file" id="slidecon-pdf" accept=".pdf" hidden />
<button id="slidecon-share" type="button">
  📑 Share sermon to SlideCon
</button>

<script>
  // 1. Stash your SlideCon API key (slcn_live_…) on your server.
  //    Never ship it inside frontend JS. The snippet below assumes
  //    you proxy through your own backend at /your-proxy/slidecon.
  document.getElementById('slidecon-share').onclick = () =>
    document.getElementById('slidecon-pdf').click();

  document.getElementById('slidecon-pdf').onchange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', file.name.replace(/\\.pdf$/i, ''));
    fd.append('source_app', 'lumera');

    const res = await fetch('/your-proxy/slidecon/from-pdf', {
      method: 'POST',
      body: fd,
    });
    const data = await res.json();
    if (data.edit_url) {
      // Open the SlideCon editor in a new tab
      window.open(data.edit_url, '_blank');
    } else {
      alert('Upload failed: ' + (data.detail || 'unknown'));
    }
  };
</script>`;
                    navigator.clipboard.writeText(html);
                    toast.success(t("copied") || "Copied!");
                  }}
                  data-testid="copy-button-snippet"
                >
                  <Copy className="w-3 h-3" /> {t("copy") || "Copy"}
                </Button>
              </div>
              <pre className="text-[11px] bg-slate-950 text-slate-100 p-3 rounded-lg overflow-x-auto max-h-64" data-testid="button-snippet">
{`<!-- Drop into any page. Proxies through your backend so the key stays secret. -->
<input type="file" id="slidecon-pdf" accept=".pdf" hidden />
<button id="slidecon-share">📑 Share sermon to SlideCon</button>

<script>
  document.getElementById('slidecon-share').onclick = () =>
    document.getElementById('slidecon-pdf').click();
  document.getElementById('slidecon-pdf').onchange = async (e) => {
    const fd = new FormData();
    fd.append('file', e.target.files[0]);
    fd.append('source_app', 'lumera');
    const res = await fetch('/your-proxy/slidecon/from-pdf', {
      method: 'POST', body: fd,
    });
    const { edit_url } = await res.json();
    if (edit_url) window.open(edit_url, '_blank');
  };
</script>`}
              </pre>
              <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                {t("proxy_note") || "Important: never expose your API key in client JS. Proxy the call through your own backend so the key stays server-side."}
              </p>
            </div>

            <p className="text-xs text-slate-500 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              <span>{t("rate_limit_note") || "Default rate limit: 100 requests/minute per key. Max file size: 50 MB. Keys can be revoked at any time and take effect immediately."}</span>
            </p>
          </CardContent>
        </Card>
      </main>

      {/* "Save your key" reveal dialog — shown once after creation */}
      <Dialog open={!!revealedKey} onOpenChange={(o) => { if (!o) setRevealedKey(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-500" />
              {t("key_created") || "API Key Created"}
            </DialogTitle>
            <DialogDescription className="text-amber-600 font-medium">
              {t("save_key_warning") || "Copy this key now — it will not be shown again."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">{t("key_name") || "Name"}</Label>
              <p className="text-sm font-semibold">{revealedKey?.name}</p>
            </div>
            <div>
              <Label className="text-xs">{t("your_new_key") || "Your new key"}</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-2 rounded bg-slate-100 dark:bg-slate-900 font-mono text-xs break-all" data-testid="revealed-key">
                  {revealedKey?.key}
                </code>
                <Button
                  variant="outline" size="sm"
                  onClick={() => copyKey(revealedKey.key, revealedKey.key_id)}
                  data-testid="copy-revealed-key"
                >
                  {copiedKeyId === revealedKey?.key_id
                    ? <Check className="w-4 h-4 text-emerald-500" />
                    : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealedKey(null)} className="bg-amber-600 hover:bg-amber-700" data-testid="done-revealing-btn">
              {t("ive_saved_it") || "I've saved it"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
